/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {Icon} from '@iconify/react';

import IDate from '@dvrd/idate';

import React from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateProductRow, BolmateSectionCard} from '../../../controls';

import {DUTCH_DATE_TIME_FORMAT} from '../../../../util/constants';

import {OverviewBuyBoxEvent} from '../../../../util/enums';

import {OverviewBuyBox} from '../../../../util/interfaces';

import {formatCurrency} from '../../../../util/utils';

import {OverviewSectionState} from '../useOverviewSection';

export default function BuyBox(props: OverviewSectionState<OverviewBuyBox>) {
    const {items, loading, loadingMore, error, hasMore, onLoadMore} = props;
    const {t} = useTranslation();

    // Prices are best-effort in the contract (decision 6): when they are absent
    // the row drops the price line instead of rendering an empty metric.
    function renderCompetitor(item: OverviewBuyBox) {
        if (item.competitor_price == null) return undefined;
        const name = item.competitor_name ?? t('overview.buyBox.anonymous', 'Anoniem');
        return `${t('overview.buyBox.competitor', 'Concurrent')}: ${formatCurrency(item.competitor_price)} (${name})`;
    }

    function renderRow(item: OverviewBuyBox, index: number) {
        const won = item.event === OverviewBuyBoxEvent.WON;
        return (
            <BolmateProductRow
                key={index}
                imageUrl={item.image_url}
                imageBadge={<Icon icon={won ? 'mdi:check-bold' : 'mdi:close-thick'} />}
                imageBadgeTitle={
                    won ? t('overview.buyBox.won', 'Koopblok gewonnen') : t('overview.buyBox.lost', 'Koopblok verloren')
                }
                imageBadgeColor={won ? 'green' : 'red'}
                title={item.title}
                titleHref={item.product_url}
                ean={item.ean}
                subtitle={new IDate(item.change_datetime).format(DUTCH_DATE_TIME_FORMAT)}
                badge={{
                    label: `${
                        won
                            ? t('overview.buyBox.won', 'Koopblok gewonnen')
                            : t('overview.buyBox.lost', 'Koopblok verloren')
                    } · ${item.country_code}`,
                    color: won ? 'green' : 'red',
                    icon: won ? 'mdi:check' : 'mdi:close'
                }}
                primaryStat={
                    item.your_price == null
                        ? undefined
                        : {label: t('overview.buyBox.yourPrice', 'Jouw prijs'), value: formatCurrency(item.your_price)}
                }
                footnote={renderCompetitor(item)}
            />
        );
    }

    return (
        <BolmateSectionCard
            title={t('overview.sections.buyBox', 'Wisseling koopblok')}
            loading={loading}
            loadingMore={loadingMore}
            empty={!items.length}
            emptyLabel={t('overview.buyBox.empty', 'Geen wisselingen in deze periode')}
            error={error ? t('overview.sectionError', 'Deze sectie kon niet geladen worden') : undefined}
            hasMore={hasMore}
            loadMoreLabel={t('overview.loadMore', 'Meer laden')}
            onLoadMore={onLoadMore}
        >
            {items.map(renderRow)}
        </BolmateSectionCard>
    );
}
